import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, User, Phone, Mail, MapPin, ChevronRight,
  Edit3, Check, X, Camera, Home as HomeIcon, Building2,
  Navigation, Plus, Trash2, Bell, Shield, HelpCircle,
  LogOut, Star, Package, Heart, Gift, Tag, Info, ExternalLink,
  CheckCircle2, Loader2, Briefcase, LocateFixed,
  Moon, Sun, Bike, Wallet, Copy, Send, AlertCircle,
  RefreshCw, Banknote, Smartphone, Clock,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryLight:"#fff7ed", primaryMid:"#fed7aa", primaryDark:"#ea580c",
  bg:"#f2f2f7", white:"#ffffff", text:"#1c1c1e", textSub:"#6b7280", textMuted:"#9ca3af",
  border:"#e5e7eb", green:"#16a34a", greenLight:"#dcfce7",
  red:"#ef4444", redLight:"#fef2f2", yellow:"#fbbf24",
  purple:"#7c3aed", purpleLight:"#f5f3ff", blue:"#2563eb", blueLight:"#eff6ff",
};

const BASE = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

const ADDR_ICON_MAP = { Home:HomeIcon, Work:Briefcase, Other:Navigation };

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function Card({ children, style={} }) {
  return (
    <div style={{ background:T.white, borderRadius:18, border:`1px solid ${T.border}`, overflow:"hidden", marginBottom:12, boxShadow:"0 1px 4px rgba(0,0,0,0.04)", ...style }}>
      {children}
    </div>
  );
}

function SLabel({ children }) {
  return (
    <div style={{ fontSize:"0.62rem", fontWeight:800, color:T.textMuted, letterSpacing:"1.8px", textTransform:"uppercase", padding:"16px 18px 6px" }}>
      {children}
    </div>
  );
}

function RowItem({ icon:Icon, iconColor=T.primary, iconBg=T.primaryLight, label, value, onClick, rightEl, danger=false, noBorder=false }) {
  return (
    <div onClick={onClick}
      style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 18px", borderTop:noBorder?"none":`1px solid ${T.border}`, cursor:onClick?"pointer":"default", transition:"background .15s" }}
      onMouseEnter={e => { if(onClick) e.currentTarget.style.background=T.bg; }}
      onMouseLeave={e => { e.currentTarget.style.background="transparent"; }}>
      <div style={{ width:40, height:40, borderRadius:12, background:iconBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Icon size={18} color={danger?T.red:iconColor} strokeWidth={1.8}/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:"0.88rem", color:danger?T.red:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{label}</div>
        {value && <div style={{ fontSize:"0.73rem", color:T.textSub, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{value}</div>}
      </div>
      {rightEl ?? (onClick && <ChevronRight size={16} color={T.textMuted} style={{ flexShrink:0 }}/>)}
    </div>
  );
}

// ── Edit Field Sheet ──────────────────────────────
function EditSheet({ title, value, onSave, onClose, type="text", placeholder="" }) {
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    await onSave(val);
    setSaving(false);
    onClose();
  };
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400, display:"flex", alignItems:"flex-end" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white, borderRadius:"22px 22px 0 0", width:"100%", maxWidth:540, margin:"0 auto", padding:"20px 20px 40px" }}>
        <div style={{ width:36, height:4, borderRadius:100, background:T.border, margin:"0 auto 20px" }}/>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", marginBottom:18 }}>{title}</div>
        <input autoFocus type={type} value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder}
          onKeyDown={e => e.key==="Enter" && handleSave()}
          style={{ width:"100%", border:`1.5px solid ${T.primary}`, borderRadius:14, padding:"13px 16px", fontSize:"0.95rem", color:T.text, background:T.bg, outline:"none", boxShadow:"0 0 0 3px rgba(249,115,22,0.1)", marginBottom:16 }}/>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onClose} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:14, padding:"12px", fontWeight:700, fontSize:"0.88rem", cursor:"pointer", color:T.textSub, fontFamily:"inherit" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:2, background:`linear-gradient(135deg,${T.primary},${T.primaryDark})`, border:"none", borderRadius:14, padding:"12px", fontWeight:800, fontSize:"0.88rem", cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7, boxShadow:"0 4px 14px rgba(249,115,22,0.3)" }}>
            {saving ? <><Loader2 size={15} style={{ animation:"spin .75s linear infinite" }}/> Saving...</> : <><Check size={15}/> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Address Sheet ─────────────────────────────
function AddAddressSheet({ onSave, onClose }) {
  const [type,     setType]    = useState("Home");
  const [house,    setHouse]   = useState("");
  const [area,     setArea]    = useState("");
  const [landmark, setLandmark]= useState("");
  const [mobile,   setMobile]  = useState("");
  const [locating, setLocating]= useState(false);
  const [saving,   setSaving]  = useState(false);
  const [errors,   setErrors]  = useState({});

  // ✅ Auto-fetch GPS when sheet opens
  useEffect(() => {
    fetchLocation();
  }, []);

  const addrTypes = [
    { label:"Home",  icon:HomeIcon   },
    { label:"Work",  icon:Briefcase  },
    { label:"Other", icon:Navigation },
  ];

  const fetchLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res  = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
          const data = await res.json();
          const addr = data.address || {};
          setArea([addr.suburb, addr.neighbourhood, addr.city_district, addr.city||addr.town].filter(Boolean).join(", ") || "");
        } catch { setArea("Location fetched — please verify"); }
        setLocating(false);
      },
      () => { alert("Location access denied."); setLocating(false); }
    );
  };

  const handleSave = async () => {
    const e = {};
    if (!house.trim())                             e.house  = "Required";
    if (!area.trim())                              e.area   = "Required";
    if (!mobile.trim())                            e.mobile = "Mobile number is required";
    else if (!/^[6-9]\d{9}$/.test(mobile.trim())) e.mobile = "Enter a valid 10-digit number";
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    await onSave({ type, line1:house.trim(), line2:area.trim(), landmark:landmark.trim(), mobile:mobile.trim() });
    setSaving(false);
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"flex-end" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white, borderRadius:"22px 22px 0 0", width:"100%", maxWidth:540, margin:"0 auto", padding:"20px 20px 44px", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ width:36, height:4, borderRadius:100, background:T.border, margin:"0 auto 20px" }}/>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.15rem" }}>Add New Address</div>
          <button onClick={onClose} style={{ background:T.bg, border:"none", borderRadius:10, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <X size={16} color={T.textSub}/>
          </button>
        </div>

        {/* Type */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:"0.7rem", fontWeight:700, color:T.textSub, marginBottom:8, textTransform:"uppercase" }}>Type</div>
          <div style={{ display:"flex", gap:8 }}>
            {addrTypes.map(({ label, icon:Icon }) => (
              <button key={label} onClick={() => setType(label)}
                style={{ flex:1, background:type===label?T.primaryLight:T.bg, border:`1.5px solid ${type===label?T.primary:T.border}`, borderRadius:12, padding:"10px 6px", display:"flex", flexDirection:"column", alignItems:"center", gap:5, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>
                <Icon size={17} color={type===label?T.primary:T.textSub} strokeWidth={1.8}/>
                <span style={{ fontSize:"0.72rem", fontWeight:700, color:type===label?T.primary:T.textSub }}>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* GPS */}
        <button onClick={fetchLocation} disabled={locating}
          style={{ width:"100%", background:T.blueLight, border:`1.5px solid #bfdbfe`, borderRadius:12, padding:"11px 16px", display:"flex", alignItems:"center", gap:10, color:T.blue, fontWeight:700, fontSize:"0.84rem", cursor:"pointer", fontFamily:"inherit", marginBottom:16 }}>
          {locating ? <Loader2 size={16} style={{ animation:"spin .75s linear infinite" }}/> : <LocateFixed size={16}/>}
          {locating ? "Detecting your location..." : area ? "📍 Refresh Location" : "📍 Detecting Location..."}
        </button>

        {/* Fields */}
        {[
          { label:"House / Flat No. *", key:"house", val:house,    set:setHouse,    ph:"e.g. A-12, Flat 302",             err:errors.house },
          { label:"Area / Street *",    key:"area",  val:area,     set:setArea,     ph:"e.g. Civil Lines, Near Bus Stand", err:errors.area  },
          { label:"Landmark (optional)",key:"land",  val:landmark, set:setLandmark, ph:"e.g. Behind SBI Bank",             err:""           },
        ].map(f => (
          <div key={f.key} style={{ marginBottom:14 }}>
            <label style={{ fontSize:"0.7rem", fontWeight:700, color:T.textSub, display:"block", marginBottom:5, textTransform:"uppercase" }}>{f.label}</label>
            <input value={f.val} onChange={e=>{ f.set(e.target.value); setErrors(er=>({...er,[f.key]:""})); }}
              placeholder={f.ph}
              style={{ width:"100%", border:`1.5px solid ${f.err?T.red:f.val?T.primary:T.border}`, borderRadius:12, padding:"11px 14px", fontSize:"0.88rem", background:T.bg, outline:"none", color:T.text, fontFamily:"inherit", transition:"border .15s" }}/>
            {f.err && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:3 }}>{f.err}</div>}
          </div>
        ))}

        {/* Mobile Number — mandatory */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:"0.7rem", fontWeight:700, color:T.textSub, display:"block", marginBottom:5, textTransform:"uppercase" }}>
            Mobile Number *
          </label>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:"0.88rem", color:T.textSub, fontWeight:600 }}>+91</span>
            <input
              type="tel"
              value={mobile}
              maxLength={10}
              placeholder="10-digit mobile number"
              onChange={e => { setMobile(e.target.value.replace(/\D/g,"")); setErrors(er=>({...er, mobile:""})); }}
              style={{ width:"100%", border:`1.5px solid ${errors.mobile ? T.red : mobile ? T.primary : T.border}`, borderRadius:12, padding:"11px 14px 11px 42px", fontSize:"0.88rem", background:T.bg, outline:"none", color:T.text, fontFamily:"inherit", transition:"border .15s" }}
            />
          </div>
          {errors.mobile && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:3 }}>{errors.mobile}</div>}
          <div style={{ fontSize:"0.68rem", color:T.textMuted, marginTop:4 }}>
            Delivery partner will call on this number
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{ width:"100%", background:`linear-gradient(135deg,${T.primary},${T.primaryDark})`, border:"none", borderRadius:14, padding:"13px", fontWeight:800, fontSize:"0.9rem", cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:"0 6px 20px rgba(249,115,22,0.3)", marginTop:4 }}>
          {saving ? <><Loader2 size={15} style={{ animation:"spin .75s linear infinite" }}/> Saving...</> : <><Check size={15}/> Save Address</>}
        </button>
      </div>
    </div>
  );
}

// ── Address Card ──────────────────────────────────
function AddressCard({ addr, onDelete, onSetDefault }) {
  const Icon = ADDR_ICON_MAP[addr.type] || Navigation;
  return (
    <div style={{ border:`1.5px solid ${addr.isDefault?T.primary:T.border}`, borderRadius:15, padding:"14px 16px", marginBottom:10, background:addr.isDefault?T.primaryLight:T.white, boxShadow:addr.isDefault?"0 2px 12px rgba(249,115,22,0.1)":"none" }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:12, background:addr.isDefault?T.primaryMid:T.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Icon size={18} color={addr.isDefault?T.primary:T.textSub} strokeWidth={1.8}/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4, flexWrap:"wrap" }}>
            <span style={{ fontWeight:700, fontSize:"0.88rem", color:addr.isDefault?T.primary:T.text }}>{addr.type}</span>
            {addr.isDefault && <span style={{ background:T.primaryMid, color:T.primaryDark, fontSize:"0.58rem", fontWeight:800, padding:"2px 8px", borderRadius:100 }}>DEFAULT</span>}
          </div>
          <div style={{ color:T.textSub, fontSize:"0.75rem", lineHeight:1.6 }}>
            {addr.line1}{addr.line2?`, ${addr.line2}`:""}
            {addr.landmark && <span style={{ color:T.textMuted }}>{` · Near ${addr.landmark}`}</span>}
          </div>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginTop:12 }}>
        {!addr.isDefault && (
          <button onClick={() => onSetDefault(addr.id)}
            style={{ flex:1, background:T.primaryLight, border:`1px solid ${T.primaryMid}`, color:T.primary, borderRadius:10, padding:"8px", fontWeight:700, fontSize:"0.73rem", cursor:"pointer", fontFamily:"inherit" }}>
            Set Default
          </button>
        )}
        <button onClick={() => onDelete(addr.id)}
          style={{ flex:1, background:T.redLight, border:`1px solid #fecaca`, color:T.red, borderRadius:10, padding:"8px", fontWeight:700, fontSize:"0.73rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:5, fontFamily:"inherit" }}>
          <Trash2 size={12}/> Remove
        </button>
      </div>
    </div>
  );
}

// ── Logout Confirm ────────────────────────────────
function LogoutConfirm({ onConfirm, onClose }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.white, borderRadius:22, width:"100%", maxWidth:340, padding:"28px 24px", boxShadow:"0 24px 60px rgba(0,0,0,0.18)" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:T.redLight, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
          <LogOut size={26} color={T.red}/>
        </div>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.3rem", textAlign:"center", marginBottom:8 }}>Logout?</div>
        <div style={{ color:T.textSub, fontSize:"0.83rem", textAlign:"center", marginBottom:22, lineHeight:1.65 }}>
          Quickry se logout karna chahte ho?
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onClose} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:14, padding:"12px", fontWeight:700, fontSize:"0.88rem", cursor:"pointer", color:T.text, fontFamily:"inherit" }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, background:`linear-gradient(135deg,${T.red},#dc2626)`, border:"none", borderRadius:14, padding:"12px", fontWeight:800, fontSize:"0.88rem", cursor:"pointer", color:"#fff", fontFamily:"inherit", boxShadow:"0 4px 14px rgba(239,68,68,0.3)" }}>
            Yes, Logout
          </button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  MAIN PROFILE PAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function ProfilePage() {
  const navigate  = useNavigate();
  // ✅ Use user + loading from AuthContext directly — no extra API call
  const { user, loading, logout, setUser, getToken, updateProfile } = useAuth();

  const [addresses,   setAddresses]  = useState([]);
  const [editSheet,   setEditSheet]  = useState(null);
  const [showLogout,  setShowLogout] = useState(false);
  const [showAddAddr, setShowAddAddr]= useState(false);
  const [notifOn,     setNotifOn]    = useState(true);
  const [saved,       setSaved]      = useState(null);
  const [activeTab,   setActiveTab]  = useState("profile");

  // ── Wallet state ──────────────────────────────────
  const [walletBal,   setWalletBal]  = useState(0);
  const [walletTab,   setWalletTab]  = useState("overview"); // overview | topup | history
  const [txns,        setTxns]       = useState([]);
  const [txnLoad,     setTxnLoad]    = useState(false);
  const [wAmount,     setWAmount]    = useState("");
  const [wUtr,        setWUtr]       = useState("");
  const [wSubmitting, setWSubmitting]= useState(false);
  const [wSuccess,    setWSuccess]   = useState(false);
  const [wError,      setWError]     = useState("");
  const [wCopied,     setWCopied]    = useState(false);
  const UPI_ID = "quickry@upi";
  const QUICK_AMOUNTS = [50, 100, 200, 500];

  const width    = useWindowWidth();
  const isMobile = width < 768;
  const px       = isMobile ? 14 : 20;

  const showToast = (msg) => { setSaved(msg); setTimeout(() => setSaved(null), 2400); };

  // Sync addresses from user doc
  useEffect(() => {
    if (user?.addresses?.length) {
      setAddresses(user.addresses.map(a => ({
        ...a, id:a._id||a.id||String(Date.now()), isDefault:a.isDefault||false,
      })));
    }
  }, [user]);

  // Fetch wallet balance
  useEffect(() => {
    if (!user) return;
    const token = getToken();
    fetch(`${BASE}/wallet/balance`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setWalletBal(d.balance || 0); })
      .catch(() => {});
  }, [user]);

  // Fetch wallet transactions
  const fetchTxns = async () => {
    setTxnLoad(true);
    try {
      const res  = await fetch(`${BASE}/wallet/transactions`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) setTxns(data.transactions || []);
    } catch {}
    setTxnLoad(false);
  };

  useEffect(() => {
    if (activeTab === "wallet" && walletTab === "history") fetchTxns();
  }, [activeTab, walletTab]);

  // Wallet deposit submit
  const submitDeposit = async () => {
    setWError("");
    if (!wAmount || isNaN(wAmount) || Number(wAmount) < 10) { setWError("Minimum ₹10 add karo"); return; }
    if (!wUtr.trim() || wUtr.trim().length < 6)              { setWError("Valid UTR / Transaction ID daalo"); return; }
    setWSubmitting(true);
    try {
      const res  = await fetch(`${BASE}/wallet/deposit-request`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
        body:    JSON.stringify({ amount: Number(wAmount), utr: wUtr.trim() }),
      });
      const data = await res.json();
      if (data.success) setWSuccess(true);
      else setWError(data.message || "Request submit nahi hua");
    } catch { setWError("Server error. Try again."); }
    setWSubmitting(false);
  };

  const copyUPI = () => {
    navigator.clipboard?.writeText(UPI_ID).catch(() => {});
    setWCopied(true);
    setTimeout(() => setWCopied(false), 2500);
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace:true });
  }, [loading, user]);

  const onLogout = () => { logout(); navigate("/login", { replace:true }); };

  // ── Save profile field ─────────────────────────
  const handleSave = async (field, val) => {
    const token = getToken();
    try {
      const res  = await fetch(`${BASE}/auth/complete-profile`, {
        method:"PUT",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ [field]:val }),
      });
      const data = await res.json();
      if (data.success) {
        // Update user in context
        if (updateProfile) updateProfile(data.data || data.user || { [field]:val });
        else setUser(prev => ({ ...prev, ...(data.data||data.user||{ [field]:val }) }));
        showToast("Saved!");
      } else showToast("Save failed!");
    } catch { showToast("Server error!"); }
  };

  // ── Add address ────────────────────────────────
  const handleAddAddress = async (newAddr) => {
    const token = getToken();
    try {
      const res  = await fetch(`${BASE}/auth/add-address`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify(newAddr),
      });
      const data = await res.json();
      if (data.success && data.address) {
        const addr = { ...data.address, id:data.address._id };
        setAddresses(p => [...p, addr]);
        showToast("Address saved!");
        return;
      }
    } catch {}
    // Local fallback
    setAddresses(p => [...p, { ...newAddr, id:Date.now().toString(), isDefault:addresses.length===0 }]);
    showToast("Address added!");
  };

  // ── Delete address ─────────────────────────────
  const handleDeleteAddress = async (id) => {
    setAddresses(a => a.filter(x => x.id!==id));
    showToast("Removed!");
    const token = getToken();
    try {
      await fetch(`${BASE}/auth/address/${id}`, {
        method:"DELETE",
        headers:{ Authorization:`Bearer ${token}` },
      });
    } catch {}
  };

  const setDefault = (id) => setAddresses(a => a.map(x => ({ ...x, isDefault:x.id===id })));

  const TABS = [
    { id:"profile",   label:"Profile"   },
    { id:"addresses", label:"Addresses" },
    { id:"wallet",    label:"Wallet"    },
    { id:"settings",  label:"Settings"  },
  ];

  // Loading
  if (loading) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:64, height:64, borderRadius:20, background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:`0 8px 24px rgba(249,115,22,0.35)` }}>
          <Loader2 size={28} color="#fff" style={{ animation:"spin .75s linear infinite" }}/>
        </div>
        <div style={{ color:T.textSub, fontSize:"0.86rem", fontWeight:600 }}>Loading profile...</div>
      </div>
    </div>
  );

  if (!user) return null;

  const orderCount   = user.totalOrders || 0;
  const savedAmount  = user.savedAmount  || 0;
  const loyaltyLevel = user.loyaltyLevel || (orderCount>=50?"Platinum":orderCount>=20?"Gold":orderCount>=5?"Silver":"Bronze");
  const memberSince  = user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN",{month:"short",year:"numeric"}) : "—";

  return (
    <div style={{ background:T.bg, minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif", color:T.text, paddingBottom:48 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        button,input,textarea{font-family:inherit;outline:none}
        ::-webkit-scrollbar{display:none}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideIn{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}
        .fu1{animation:fadeUp .4s .00s ease both}
        .fu2{animation:fadeUp .4s .07s ease both}
        .fu3{animation:fadeUp .4s .14s ease both}
        .fu4{animation:fadeUp .4s .21s ease both}
        .toast{animation:slideIn .3s ease both}
      `}</style>

      {/* Toast */}
      {saved && (
        <div className="toast" style={{ position:"fixed", top:72, left:"50%", transform:"translateX(-50%)", background:"#1c1c1e", color:"#fff", padding:"11px 22px", borderRadius:100, fontSize:"0.82rem", fontWeight:600, zIndex:600, display:"flex", alignItems:"center", gap:8, boxShadow:"0 8px 24px rgba(0,0,0,0.22)", whiteSpace:"nowrap" }}>
          <CheckCircle2 size={15} color={T.green}/> {saved}
        </div>
      )}

      {/* Header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:T.white, borderBottom:`1px solid ${T.border}`, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:`14px ${px+4}px`, maxWidth:900, margin:"0 auto" }}>
          <button onClick={() => navigate(-1)}
            style={{ background:T.bg, border:"none", cursor:"pointer", color:T.text, display:"flex", padding:8, borderRadius:12 }}>
            <ArrowLeft size={20}/>
          </button>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.2rem", letterSpacing:"-0.5px", flex:1 }}>My Profile</div>
          <button onClick={() => setEditSheet({ field:"name", title:"Edit Name", value:user.name||"", type:"text", placeholder:"Full name" })}
            style={{ background:T.primaryLight, border:`1px solid ${T.primaryMid}`, cursor:"pointer", color:T.primary, display:"flex", alignItems:"center", gap:5, fontSize:"0.8rem", fontWeight:700, padding:"7px 13px", borderRadius:20 }}>
            <Edit3 size={13}/> Edit
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderTop:`1px solid ${T.border}`, maxWidth:900, margin:"0 auto" }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ flex:1, background:"none", border:"none", borderBottom:`2.5px solid ${activeTab===tab.id?T.primary:"transparent"}`, padding:"11px 6px", fontWeight:activeTab===tab.id?700:500, fontSize:"0.83rem", color:activeTab===tab.id?T.primary:T.textSub, cursor:"pointer", transition:"all .2s", fontFamily:"inherit" }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:900, margin:"0 auto", padding:`16px ${px}px` }}>

        {/* ══ PROFILE TAB ══ */}
        {activeTab === "profile" && (
          <>
            <Card className="fu1" style={{ overflow:"hidden" }}>
              {/* Banner */}
              <div style={{ height:72, background:`linear-gradient(135deg,${T.primaryDark},${T.primary},#fb923c)`, position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", inset:0, opacity:.12, backgroundImage:"radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize:"18px 18px" }}/>
                <div style={{ position:"absolute", right:-20, top:-20, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.08)" }}/>
              </div>

              <div style={{ padding:"0 18px 18px" }}>
                <div style={{ position:"relative", display:"inline-block", marginTop:-34, marginBottom:12 }}>
                  <div style={{ width:68, height:68, borderRadius:"50%", background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, display:"flex", alignItems:"center", justifyContent:"center", border:`3px solid ${T.white}`, boxShadow:`0 4px 16px rgba(249,115,22,0.3)` }}>
                    <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.9rem", color:"#fff" }}>
                      {user.name?.charAt(0)?.toUpperCase()||"?"}
                    </span>
                  </div>
                  <button style={{ position:"absolute", bottom:0, right:0, width:24, height:24, borderRadius:"50%", background:T.white, border:`1.5px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.1)" }}>
                    <Camera size={11} color={T.primary}/>
                  </button>
                </div>

                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.3rem", letterSpacing:"-0.5px", marginBottom:3 }}>{user.name||"—"}</div>
                <div style={{ color:T.textSub, fontSize:"0.78rem", marginBottom:10 }}>{user.email||`+91 ${user.phone}`}</div>

                <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#fef9c3", border:"1px solid #fde047", borderRadius:100, padding:"4px 11px" }}>
                  <Star size={11} fill={T.yellow} color={T.yellow}/>
                  <span style={{ fontSize:"0.68rem", fontWeight:800, color:"#92400e" }}>{loyaltyLevel} Member</span>
                </div>
              </div>

              {/* Stats */}
              <div style={{ borderTop:`1px solid ${T.border}`, display:"flex" }}>
                {[
                  { label:"Orders",  value:orderCount,          icon:Package, color:T.primary  },
                  { label:"Saved",   value:`Rs.${savedAmount}`, icon:Tag,     color:T.green    },
                  { label:"Since",   value:memberSince,          icon:Star,    color:"#b45309"  },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} style={{ flex:1, padding:"14px 8px", textAlign:"center", borderLeft:i>0?`1px solid ${T.border}`:"none" }}>
                      <Icon size={14} color={s.color} style={{ margin:"0 auto 5px", display:"block" }}/>
                      <div style={{ fontWeight:800, fontSize:"0.95rem", color:T.text }}>{s.value}</div>
                      <div style={{ fontSize:"0.6rem", color:T.textMuted, marginTop:1, fontWeight:600 }}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="fu2">
              <SLabel>Personal Info</SLabel>
              <RowItem icon={User}  iconColor={T.primary} iconBg={T.primaryLight} label="Full Name"     value={user.name||"—"}                         onClick={() => setEditSheet({ field:"name",  title:"Edit Name",    value:user.name||"",  type:"text",  placeholder:"Full name"          })} noBorder/>
              <RowItem icon={Phone} iconColor={T.blue}    iconBg={T.blueLight}   label="Mobile Number" value={user.phone?`+91 ${user.phone}`:"Not added"} onClick={() => setEditSheet({ field:"phone", title:"Phone Number",  value:user.phone||"", type:"tel",   placeholder:"10-digit number"    })}/>
              <RowItem icon={Mail}  iconColor={T.purple}  iconBg={T.purpleLight} label="Email Address"  value={user.email||"Not added"}                 onClick={() => setEditSheet({ field:"email", title:"Edit Email",   value:user.email||"", type:"email", placeholder:"yourname@email.com" })}/>
            </Card>

            <Card className="fu3">
              <SLabel>My Activity</SLabel>
              <RowItem icon={Package} iconColor={T.primary} iconBg={T.primaryLight} label="My Orders"    value={orderCount>0?`${orderCount} orders placed`:"No orders yet"} onClick={() => navigate("/orders")} noBorder/>
              <RowItem icon={Heart}   iconColor={T.red}     iconBg={T.redLight}     label="Favourites"   value="Saved restaurants"                                        onClick={() => {}}/>
              <RowItem icon={Gift}    iconColor="#b45309"   iconBg="#fef3c7"         label="Rewards"      value={savedAmount>0?`Rs.${savedAmount} saved`:"Earn rewards on orders"} onClick={() => {}}/>
            </Card>
          </>
        )}

        {/* ══ ADDRESSES TAB ══ */}
        {activeTab === "addresses" && (
          <div className="fu1">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.05rem", marginBottom:14, padding:"4px 2px" }}>Saved Addresses</div>

            {addresses.length === 0 ? (
              <Card style={{ padding:"36px 20px", textAlign:"center" }}>
                <div style={{ width:60, height:60, borderRadius:"50%", background:T.primaryLight, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
                  <MapPin size={26} color={T.primary} strokeWidth={1.5}/>
                </div>
                <div style={{ fontWeight:700, color:T.text, fontSize:"0.93rem", marginBottom:5 }}>No saved addresses</div>
                <div style={{ fontSize:"0.77rem", color:T.textMuted, lineHeight:1.6 }}>Add your home or work address for faster checkout</div>
              </Card>
            ) : (
              addresses.map(addr => (
                <AddressCard key={addr.id} addr={addr} onDelete={handleDeleteAddress} onSetDefault={setDefault}/>
              ))
            )}

            <button onClick={() => setShowAddAddr(true)}
              style={{ width:"100%", background:T.white, border:`1.5px dashed ${T.primary}`, borderRadius:15, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:T.primary, fontWeight:700, fontSize:"0.86rem", cursor:"pointer", fontFamily:"inherit", marginTop:4, transition:"background .15s" }}
              onMouseEnter={e => e.currentTarget.style.background=T.primaryLight}
              onMouseLeave={e => e.currentTarget.style.background=T.white}>
              <Plus size={16}/> Add New Address
            </button>

            <div style={{ marginTop:14, background:`linear-gradient(135deg,${T.primaryLight},#fff7ed)`, border:`1px solid ${T.primaryMid}`, borderRadius:14, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:38, height:38, borderRadius:12, background:T.primaryMid, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Bike size={17} color={T.primary}/>
              </div>
              <div style={{ fontSize:"0.78rem", color:T.primaryDark, lineHeight:1.6 }}>
                <strong>Quickry delivers in Akola!</strong><br/>Civil Lines, Sindhi Camp, Ramdaspeth &amp; more.
              </div>
            </div>
          </div>
        )}

        {/* ══ WALLET TAB ══ */}
        {activeTab === "wallet" && (
          <div className="fu1">
            {/* Balance Banner */}
            <Card style={{ background:"linear-gradient(135deg,#052e16,#0d4a24)", border:"1px solid rgba(22,163,74,0.3)", overflow:"visible" }}>
              <div style={{ padding:"20px 18px" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:40, height:40, borderRadius:12, background:"rgba(22,163,74,0.2)", border:"1px solid rgba(22,163,74,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <Wallet size={19} color={T.green}/>
                    </div>
                    <div>
                      <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"0.95rem", color:"#f0fdf4" }}>Quickry Wallet</div>
                      <div style={{ fontSize:"0.62rem", color:"rgba(134,239,172,0.7)" }}>Secure · Instant · Easy</div>
                    </div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:"0.6rem", fontWeight:700, color:"rgba(134,239,172,0.7)", letterSpacing:"1px", textTransform:"uppercase" }}>Balance</div>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.9rem", color:"#f0fdf4", letterSpacing:"-1px" }}>₹{walletBal}</div>
                  </div>
                </div>
                <button onClick={() => setWalletTab("topup")}
                  style={{ width:"100%", background:"linear-gradient(135deg,#166534,#16a34a)", border:"none", borderRadius:12, padding:"12px", fontWeight:700, fontSize:"0.88rem", color:"#fff", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  <Plus size={15}/> Add Money to Wallet
                </button>
              </div>
            </Card>

            {/* Sub Tabs */}
            <div style={{ display:"flex", background:T.white, borderRadius:14, padding:4, border:`1px solid ${T.border}`, marginBottom:12 }}>
              {[{ id:"overview", label:"Overview" }, { id:"topup", label:"Add Money" }, { id:"history", label:"History" }].map(t => (
                <button key={t.id} onClick={() => setWalletTab(t.id)}
                  style={{ flex:1, padding:"9px 8px", borderRadius:10, border:"none", background:walletTab===t.id?T.bg:"transparent", color:walletTab===t.id?T.text:T.textSub, fontWeight:walletTab===t.id?700:400, fontSize:"0.77rem", cursor:"pointer", fontFamily:"inherit", transition:"all .2s" }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* OVERVIEW */}
            {walletTab === "overview" && (
              <Card>
                <SLabel>How it works</SLabel>
                <div style={{ padding:"8px 18px 18px" }}>
                  {[
                    { step:"01", text:"GPay / PhonePe se UPI payment karo", icon:Smartphone, color:T.blue   },
                    { step:"02", text:"UTR number yahan enter karo",          icon:Copy,       color:T.purple },
                    { step:"03", text:"Admin verify karega — 10 min mein",    icon:Shield,     color:"#d97706"},
                    { step:"04", text:"Wallet mein amount credit ho jayega",  icon:CheckCircle2, color:T.green },
                  ].map((s, i) => (
                    <div key={s.step} style={{ display:"flex", alignItems:"center", gap:14, marginBottom:i<3?14:0 }}>
                      <div style={{ width:38, height:38, borderRadius:12, background:s.color+"18", border:`1px solid ${s.color}30`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <s.icon size={17} color={s.color}/>
                      </div>
                      <div>
                        <span style={{ fontSize:"0.6rem", fontWeight:700, color:T.textMuted, marginRight:6 }}>{s.step}</span>
                        <span style={{ fontSize:"0.82rem", color:T.textSub }}>{s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* ADD MONEY */}
            {walletTab === "topup" && (
              wSuccess ? (
                <Card style={{ padding:"32px 20px", textAlign:"center" }}>
                  <div style={{ width:68, height:68, borderRadius:"50%", background:T.greenLight, border:`2px solid ${T.green}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                    <CheckCircle2 size={32} color={T.green}/>
                  </div>
                  <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.2rem", marginBottom:6 }}>Request Submitted!</div>
                  <div style={{ fontSize:"0.82rem", color:T.textSub, lineHeight:1.7, marginBottom:20 }}>
                    Admin verify karega.<br/>10 minutes mein wallet mein credit hoga.
                  </div>
                  <button onClick={() => { setWSuccess(false); setWAmount(""); setWUtr(""); }}
                    style={{ background:T.primaryLight, border:`1px solid ${T.primaryMid}`, borderRadius:12, padding:"10px 24px", color:T.primary, fontWeight:700, fontSize:"0.85rem", cursor:"pointer", fontFamily:"inherit" }}>
                    Naya Request Karo
                  </button>
                </Card>
              ) : (
                <>
                  {/* Step 1 — UPI */}
                  <Card>
                    <SLabel>Step 1 — UPI se Payment Karo</SLabel>
                    <div style={{ padding:"8px 18px 18px" }}>
                      <div style={{ background:T.bg, borderRadius:12, padding:"14px", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                        <div>
                          <div style={{ fontSize:"0.62rem", color:T.textMuted, fontWeight:600, marginBottom:3 }}>UPI ID</div>
                          <div style={{ fontWeight:800, fontSize:"0.95rem", color:T.blue }}>{UPI_ID}</div>
                        </div>
                        <button onClick={copyUPI}
                          style={{ background:wCopied?T.greenLight:T.blueLight, border:`1px solid ${wCopied?T.green:T.blue}40`, borderRadius:10, padding:"8px 14px", color:wCopied?T.green:T.blue, fontWeight:700, fontSize:"0.75rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"inherit" }}>
                          {wCopied ? <><CheckCircle2 size={13}/> Copied!</> : <><Copy size={13}/> Copy</>}
                        </button>
                      </div>
                      <div style={{ fontSize:"0.72rem", color:T.textSub, lineHeight:1.6 }}>
                        GPay, PhonePe ya koi bhi UPI app se <strong>{UPI_ID}</strong> pe payment karo
                      </div>
                    </div>
                  </Card>

                  {/* Step 2 — Amount */}
                  <Card>
                    <SLabel>Step 2 — Amount Daalo</SLabel>
                    <div style={{ padding:"8px 18px 18px" }}>
                      <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                        {QUICK_AMOUNTS.map(a => (
                          <button key={a} onClick={() => setWAmount(String(a))}
                            style={{ flex:1, padding:"8px 4px", borderRadius:10, border:`1.5px solid ${wAmount===String(a)?T.primary:T.border}`, background:wAmount===String(a)?T.primaryLight:"transparent", color:wAmount===String(a)?T.primary:T.textSub, fontWeight:700, fontSize:"0.75rem", cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>
                            ₹{a}
                          </button>
                        ))}
                      </div>
                      <input type="number" value={wAmount} onChange={e => setWAmount(e.target.value)}
                        placeholder="Ya custom amount (min ₹10)"
                        style={{ width:"100%", border:`1.5px solid ${wAmount?T.primary:T.border}`, borderRadius:12, padding:"12px 14px", fontSize:"0.88rem", color:T.text, background:T.bg, fontFamily:"inherit", outline:"none" }}/>
                    </div>
                  </Card>

                  {/* Step 3 — UTR */}
                  <Card>
                    <SLabel>Step 3 — UTR / Transaction ID Daalo</SLabel>
                    <div style={{ padding:"8px 18px 18px" }}>
                      <input type="text" value={wUtr} onChange={e => setWUtr(e.target.value)}
                        placeholder="e.g. 407123456789"
                        style={{ width:"100%", border:`1.5px solid ${wUtr?T.primary:T.border}`, borderRadius:12, padding:"12px 14px", fontSize:"0.88rem", color:T.text, background:T.bg, fontFamily:"inherit", outline:"none", marginBottom:8 }}/>
                      <div style={{ fontSize:"0.68rem", color:T.textMuted, display:"flex", alignItems:"center", gap:5 }}>
                        <AlertCircle size={11}/> GPay app → Payment history → Transaction details mein milta hai
                      </div>
                    </div>
                  </Card>

                  {wError && (
                    <div style={{ background:T.redLight, border:`1px solid #fecaca`, borderRadius:12, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                      <AlertCircle size={14} color={T.red}/>
                      <span style={{ fontSize:"0.8rem", color:T.red, fontWeight:600 }}>{wError}</span>
                    </div>
                  )}

                  <button onClick={submitDeposit} disabled={wSubmitting}
                    style={{ width:"100%", background:wSubmitting?T.bg:`linear-gradient(135deg,${T.green},#15803d)`, border:`1px solid ${wSubmitting?T.border:"transparent"}`, borderRadius:14, padding:"14px", fontWeight:800, fontSize:"0.92rem", color:wSubmitting?T.textSub:"#fff", cursor:wSubmitting?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit", transition:"all .2s" }}>
                    {wSubmitting ? <><Loader2 size={17} style={{ animation:"spin .75s linear infinite" }}/> Submitting...</> : <><Send size={16}/> Submit Deposit Request</>}
                  </button>
                  <div style={{ textAlign:"center", marginTop:10, fontSize:"0.68rem", color:T.textMuted, lineHeight:1.8 }}>
                    Admin 10 minutes mein approve karega • Approved hone par wallet mein dikhega
                  </div>
                </>
              )
            )}

            {/* HISTORY */}
            {walletTab === "history" && (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>Transaction History</div>
                  <button onClick={fetchTxns} style={{ background:"none", border:"none", cursor:"pointer", color:T.textSub, display:"flex", alignItems:"center", gap:5, fontSize:"0.75rem", fontFamily:"inherit" }}>
                    <RefreshCw size={13} style={{ animation:txnLoad?"spin .75s linear infinite":"none" }}/> Refresh
                  </button>
                </div>
                {txnLoad ? (
                  <div style={{ textAlign:"center", padding:40 }}><Loader2 size={28} color={T.primary} style={{ animation:"spin .75s linear infinite" }}/></div>
                ) : txns.length === 0 ? (
                  <Card style={{ padding:"36px 20px", textAlign:"center" }}>
                    <Wallet size={44} color={T.border} strokeWidth={1} style={{ margin:"0 auto 12px", display:"block" }}/>
                    <div style={{ fontWeight:700, color:T.textSub, fontSize:"0.88rem" }}>Koi transactions nahi abhi tak</div>
                  </Card>
                ) : txns.map((t, i) => {
                  const isCredit  = t.type === "credit";
                  const isPending = t.status === "pending";
                  const sc = isPending ? T.yellow : isCredit ? T.green : T.red;
                  return (
                    <div key={t._id||i} style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14, padding:"14px 16px", marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                            <div style={{ width:30, height:30, borderRadius:"50%", background:sc+"18", display:"flex", alignItems:"center", justifyContent:"center" }}>
                              {isCredit ? <Banknote size={14} color={sc}/> : <Send size={14} color={sc}/>}
                            </div>
                            <span style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>
                              {t.description || (isCredit ? "Wallet Credit" : "Wallet Debit")}
                            </span>
                          </div>
                          {t.utr && <div style={{ fontSize:"0.65rem", color:T.textMuted, paddingLeft:38 }}>UTR: {t.utr}</div>}
                          <div style={{ fontSize:"0.63rem", color:T.textMuted, marginTop:2, paddingLeft:38 }}>
                            {new Date(t.createdAt).toLocaleString("en-IN")}
                          </div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontWeight:800, fontSize:"0.95rem", color:isCredit?T.green:T.red }}>
                            {isCredit?"+":"-"}₹{t.amount}
                          </div>
                          <div style={{ fontSize:"0.6rem", fontWeight:700, marginTop:4, padding:"2px 8px", borderRadius:100, background:sc+"15", color:sc }}>
                            {isPending?"PENDING":t.status?.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ SETTINGS TAB ══ */}
        {activeTab === "settings" && (
          <>
            <Card className="fu1">
              <SLabel>Preferences</SLabel>
              <div style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 18px" }}>
                <div style={{ width:40, height:40, borderRadius:12, background:T.primaryLight, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Bell size={18} color={T.primary} strokeWidth={1.8}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:"0.88rem", color:T.text }}>Order Notifications</div>
                  <div style={{ fontSize:"0.72rem", color:T.textSub, marginTop:2 }}>SMS &amp; app alerts for orders</div>
                </div>
                <div onClick={() => setNotifOn(n=>!n)}
                  style={{ width:46, height:26, borderRadius:100, background:notifOn?T.primary:T.border, position:"relative", cursor:"pointer", transition:"background .25s", boxShadow:notifOn?`0 2px 8px rgba(249,115,22,0.3)`:"none" }}>
                  <div style={{ position:"absolute", top:3, left:notifOn?23:3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left .25s", boxShadow:"0 1px 4px rgba(0,0,0,0.2)" }}/>
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 18px", borderTop:`1px solid ${T.border}` }}>
                <div style={{ width:40, height:40, borderRadius:12, background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Sun size={18} color={T.yellow} strokeWidth={1.8}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:"0.88rem", color:T.text }}>Dark Mode</div>
                  <div style={{ fontSize:"0.72rem", color:T.textSub, marginTop:2 }}>Coming soon!</div>
                </div>
                <div style={{ width:46, height:26, borderRadius:100, background:T.border, position:"relative", opacity:.45 }}>
                  <div style={{ position:"absolute", top:3, left:3, width:20, height:20, borderRadius:"50%", background:"#fff" }}/>
                </div>
              </div>
            </Card>

            <Card className="fu2">
              <SLabel>Support</SLabel>
              <RowItem icon={HelpCircle} iconColor={T.blue}   iconBg={T.blueLight}   label="Help &amp; Support" value="FAQs, chat with us"    onClick={() => {}} noBorder/>
              <RowItem icon={Shield}     iconColor={T.green}  iconBg={T.greenLight}  label="Privacy Policy"     value="How we use your data"  onClick={() => {}}/>
              <RowItem icon={Info}       iconColor={T.purple} iconBg={T.purpleLight} label="Terms of Service"                                 onClick={() => {}}/>
              <RowItem icon={Star}       iconColor={T.yellow} iconBg="#fef9c3"       label="Rate the App"       value="Love Quickry?"         onClick={() => {}}/>
            </Card>

            <Card className="fu3">
              <SLabel>About</SLabel>
              <RowItem icon={ExternalLink} iconColor={T.textSub} iconBg={T.bg} label="App Version" value="v1.0.0" noBorder
                rightEl={<span style={{ fontSize:"0.7rem", color:T.green, background:T.greenLight, padding:"3px 9px", borderRadius:100, fontWeight:700 }}>Up to date</span>}/>
            </Card>

            <Card className="fu4">
              <RowItem icon={LogOut} iconColor={T.red} iconBg={T.redLight} label="Logout" onClick={() => setShowLogout(true)} danger noBorder
                rightEl={<ChevronRight size={16} color={T.red}/>}/>
            </Card>

            <div style={{ textAlign:"center", padding:"14px 0 4px" }}>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.15rem" }}>
                <span style={{ color:T.primary }}>Quick</span><span style={{ color:T.primaryDark }}>ry</span>
              </div>
              <div style={{ fontSize:"0.68rem", color:T.textMuted, marginTop:4 }}>Made with love in Akola</div>
            </div>
          </>
        )}
      </div>

      {showAddAddr && <AddAddressSheet onSave={handleAddAddress} onClose={() => setShowAddAddr(false)}/>}
      {editSheet   && <EditSheet {...editSheet} onSave={val => handleSave(editSheet.field, val)} onClose={() => setEditSheet(null)}/>}
      {showLogout  && <LogoutConfirm onConfirm={() => { setShowLogout(false); onLogout(); }} onClose={() => setShowLogout(false)}/>}
    </div>
  );
}